import { format } from "date-fns";
import { Column } from "../objects/column";
import { DaysString, OperationalOpenType, TransferLocalized, VehicleTransferBasicDataServiceTypeValue } from "../objects/transfer";
import { TransferCancellationRuleListField } from "../objects/transferCancellationRuleListField";
import { TransferFormType } from "../objects/transferForm";
import { TransferListConfiguration } from "../objects/transferListConfiguration";
import { TransferListCriteria } from "../objects/transferListCriteria";
import { TransferListFiltersCriteria } from "../objects/transferListFiltersCriteria";
import * as locales from 'date-fns/locale';
import i18next from "i18next";
import { CancellationRule } from "../objects/cancellationRule";
import { Rule } from "../objects/rule";
import { SegmentTransferLocation, SegmentFormType, SegmentLocationType } from "../objects/Segment";
import { Place } from "../../place/objects/place";
import { PricingType, TransferContractParameters } from "../objects/transferContractParameters";
import { NewPriceForms, TransferPriceUtil, TransferPriceUtilGroupedByDate, TransferPriceUtilGroupedByDay } from "../objects/transferPrice";
import { TransferAvailability, TransferAvailabilityParameters } from "../objects/transferAvailability";



const EMPTY_OPERATIONAL_HOUR = ["08:00-17:00"];

export const EMPTY_OPERATIONAL_HOURS = {
    [DaysString.MONDAY]: EMPTY_OPERATIONAL_HOUR,
    [DaysString.TUESDAY]: EMPTY_OPERATIONAL_HOUR,
    [DaysString.WEDNESDAY]: EMPTY_OPERATIONAL_HOUR,
    [DaysString.THURSDAY]: EMPTY_OPERATIONAL_HOUR,
    [DaysString.FRIDAY]: EMPTY_OPERATIONAL_HOUR,
    [DaysString.SATURDAY]: EMPTY_OPERATIONAL_HOUR,
    [DaysString.SUNDAY]: EMPTY_OPERATIONAL_HOUR
};

export const EMPTY_AVAILABILITY_PARAMETERS: TransferAvailabilityParameters = {
    operational_open_type: OperationalOpenType.ALL_DAYS,
    transfer_availability_duration: []
};

export const EMPTY_AVAILABILITY_DURATION: TransferAvailability = {
    start_date: null,
    end_date: null,
    operational_days: [],
    operational_hours: EMPTY_OPERATIONAL_HOURS
};

export const EMPTY_TRANSFER_LOCALIZED: TransferLocalized = {
    locale: 0,
    title: "",
    description: "",
    voucher_remark: ""
};

export const EMPTY_PRICE_FORM: NewPriceForms = {
    catalog_price: false,
    price_ranges: []
};

export const EMPTY_PRICE_GROUPED_BY_DAY: TransferPriceUtilGroupedByDay = {
    purchase_price: "",
    purchase_price_error: "",
    selling_price: "",
    selling_price_error: "",
    com_price: "",
    markup_price: "",
    hours_selected: ['08:00-17:00'],
    values: [],
    price_ids: [],
};

export const EMPTY_PRICE_GROUPED_BY_DATE: TransferPriceUtilGroupedByDate = {
    day_group_number: 0,
    operational_days: null,
    price_grouped_by_days: [EMPTY_PRICE_GROUPED_BY_DAY],
};

export const EMPTY_TRANSFER_PRICE_UTIL: TransferPriceUtil = {
    model: null,
    is_price_by_day: false,
    date_group_number: 0,
    model_group_number: 0,
    price_grouped_by_date: [EMPTY_PRICE_GROUPED_BY_DATE],
};


export const EMPTY_CONTRACT_PARAMETERS: TransferContractParameters = {
    pricing_type: PricingType.PURCHASE,
    com_is_percentage: false,
    global_com: "",
    com_calculation_as_percentage: false,
    markup_is_percentage: false,
    global_markup: "",
    com_applied_on_ttc: false
};

export const EMPTY_PLACE: Place = {
    address: "",
    city_name: "",
    zip_code: "",
    latitude: '48.8596169155',
    longitude: '2.3564089625',
    google_id: "",
    zoom: 11,
    country_name: null,
    displayed_destination: '',
    id: 0
};

export const EMPTY_SEGMENT_TRANSFER_LOCATION: SegmentTransferLocation = {
    linked_hotels: [],
    linked_airport: null,
    linked_station: null,
    linked_port: null,
    linked_destination: null,
    localization: []
};

export const EMPTY_SEGMENT_FORM: SegmentFormType = {
    id: -1,
    country: null,
    is_round_trip: true,

    origin_name: "",
    going_radius: 5,
    origin_geo_location: EMPTY_PLACE,
    origin_location_type: SegmentLocationType.CITY_ZONE,
    origin_transfer_location: EMPTY_SEGMENT_TRANSFER_LOCATION,

    destination_name: "",
    return_radius: 5,
    destination_geo_location: EMPTY_PLACE,
    destination_location_type: SegmentLocationType.CITY_ZONE,
    destination_transfer_location: EMPTY_SEGMENT_TRANSFER_LOCATION,

    duration: ''

};


export const EMPTY_TRANSFER_FORM: TransferFormType = {
    model: null,
    vehicle_model: null,
    contracts: [],
    segment: null,
    id: 0,
    end_date: null,
    start_date: null,
    prices: [],
    destination: null,

    visibility: 'PUBLIC',

    title: "",
    title_locales: {},

    voucher_remark_locales: {},
    voucher_remark: '',
    description_locales: {},
    description: '',

    localization: [],
    is_active: true,
    number_of_releases: 1,
    service_type: VehicleTransferBasicDataServiceTypeValue.PRIVATE,
    // pickup_time_before_departure: '',
    transfer_in_from_transport: false,
    operational_open_type: OperationalOpenType.ALL_DAYS,
    operational_days: [],
    operational_hours: EMPTY_OPERATIONAL_HOURS,
    schedule_times: [],
    search_engines: [],
    deactivated_contracts: [],
    providers: [],
    contract_form: [{ contract: null, provider: null }],
};

export const DAY_TO_NUMBER: Record<string, number> = {
    [DaysString.MONDAY]: 0,
    [DaysString.TUESDAY]: 1,
    [DaysString.WEDNESDAY]: 2,
    [DaysString.THURSDAY]: 3,
    [DaysString.FRIDAY]: 4,
    [DaysString.SATURDAY]: 5,
    [DaysString.SUNDAY]: 6
};

export const NUMBER_TO_DAY: DaysString[] = [
    DaysString.MONDAY,
    DaysString.TUESDAY,
    DaysString.WEDNESDAY,
    DaysString.THURSDAY,
    DaysString.FRIDAY,
    DaysString.SATURDAY,
    DaysString.SUNDAY
];

export const EMPTY_TRANSFER_FORM_FILTERS: TransferListFiltersCriteria = {
    creator: "",
    last_author: "",
    created_date_gte: null,
    created_date_lte: null,
    modified_date_gte: null,
    modified_date_lte: null,
    vehicle_type: '',
    period_type: 0,
    period: "MODIFICATION",
    service_type: 'ALL',
    provider: null,
    status: "ALL",
    origin: "",
    destination: "",
    cms: []
};

export const EMPTY_TRANSFER_FORM_LIST_CRITERIA: TransferListCriteria = {
    limit: 50,
    page: 0,
    order_by: '',
    order: 'asc',
    filters: EMPTY_TRANSFER_FORM_FILTERS,
    search: ''
};

export const TRANSFER_INFO_ROUTE = '/transfer/edit/:id/info';
export const TRANSFER_CONTACT_ROUTE = '/transfer/edit/:id/contact';

export const DEFAULT_COLUMN: Column[] = [
    {
        label: 'name',
        field_name: 'NAME',
        checked: true,
        order_by: 'title',
        fixed_width: false,
        last_updated: false,
        width: null
    },
    {
        label: 'providers',
        field_name: 'PROVIDERS',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false,
        width: null
    },
    {
        label: 'origin',
        field_name: 'ORIGIN',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false,
        width: null
    },
    {
        label: 'destination',
        field_name: 'DESTINATION',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false,
        width: null
    },
    {
        label: 'product-type',
        field_name: 'PRODUCT_TYPE',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false,
        width: null
    },
    {
        label: 'service-type',
        field_name: 'SERVICE_TYPE',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false,
        width: null
    },
    {
        label: 'vehicle-type',
        field_name: 'VEHICLE_TYPE',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false,
        width: null
    },
    {
        label: 'created-date',
        field_name: 'CREATED_DATE',
        checked: false,
        order_by: 'created_date',
        fixed_width: true,
        last_updated: false,
        width: null
    }, {
        label: 'online-to',
        field_name: 'VISIBILITY',
        checked: true,
        order_by: '',
        fixed_width: true,
        last_updated: false,
        width: null
    }, {
        label: 'online-agency',
        field_name: 'AGENCY_VISIBILITY',
        checked: true,
        order_by: '',
        fixed_width: true,
        last_updated: false,
        width: null
    }, {
        label: 'allowed-cms',
        field_name: 'ALLOWED_CMS',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false,
        width: null
    }, {
        label: 'created-by',
        field_name: 'CREATED_BY',
        checked: false,
        order_by: 'creator__username',
        fixed_width: true,
        last_updated: false,
        width: null
    }, {
        label: 'modified-date',
        field_name: 'MODIFIED_DATE',
        checked: true,
        order_by: 'modified_date',
        fixed_width: true,
        last_updated: false,
        width: null
    }, {
        label: 'modified-by',
        field_name: 'MODIFIED_BY',
        checked: false,
        order_by: 'last_author__username',
        fixed_width: true,
        last_updated: false,
        width: null
    }];

export const EMPTY_TRANSFER_FORM_LIST_CONFIGURATION: TransferListConfiguration = {
    columns: DEFAULT_COLUMN,
    preferences: []
};

export const TRANSFER_MODULE = 'TRANSFER';




const locales_cpy: { [key: string]: any } = locales;

export const CANCELLATION_RULE_LIST_FIELD: TransferCancellationRuleListField[] = [
    {
        field_name: "name",
        label: "name",
        sortable: false,
        getValue: (cancellation_rule) => cancellation_rule.name || '',
    },
    {
        field_name: "reference",
        label: "reference",
        sortable: true,
        getValue: (cancellation_rule) => cancellation_rule.reference,
    },
    {
        field_name: "created_date",
        label: "created-date",
        sortable: true,
        getValue: (cancellation_rule) => cancellation_rule.created_date !== null ? format(new Date(cancellation_rule.created_date), 'P HH:mm', { locale: locales_cpy[i18next.language.replace("-", "")] }) : '',
    },
    {
        field_name: "creator",
        label: "created-by",
        sortable: true,
        getValue: (cancellation_rule) => cancellation_rule.creator?.username || '',
    },
    {
        field_name: "modified_date",
        label: "modified-date",
        sortable: true,
        getValue: (cancellation_rule) => cancellation_rule.modified_date !== null ? format(new Date(cancellation_rule.modified_date), 'P HH:mm', { locale: locales_cpy[i18next.language.replace("-", "")] }) : '',
    },
    {
        field_name: "last_author",
        label: "modified-by",
        sortable: true,
        getValue: (cancellation_rule) => cancellation_rule.last_author?.username || '',
    },
];


export const EMPTY_CANCELLATION_RULE: CancellationRule = {
    id: -1,
    product: 0,
    contract: undefined,
    created_date: null,
    creator: null,
    free_cancel: 0,
    last_author: null,
    localization: [],
    modified_date: null,
    name: null,
    rules: [],
    reference: "",
    periods: [{ start_date: null, end_date: null }]
};

export const EMPTY_CANCELLATION_CONDITION: Rule = {
    nb_days: 0,
    price_type: 'FIX',
    value: 0,
    cancellation_type: 0
};